import dagre from 'dagre';
import type { ProcessEdge, ProcessNode } from '../graphTypes';

/**
 * Default node footprint for the layout pass. The real node DOM has
 * variable height (Task vs. Input vs. Gate; collapsed vs. expanded), but
 * dagre only needs an approximation -- it uses these to space ranks
 * apart, not to position individual handles. The values are slightly
 * generous so dense graphs don't end up with overlapping cards even
 * when a few nodes are taller than the typical task card.
 */
const DEFAULT_NODE_WIDTH = 280;
const DEFAULT_NODE_HEIGHT = 160;

/**
 * Approximate per-port height increment. Multi-port nodes (e.g. a task
 * with 1 input + 2 outputs, or a gate with approve/reject branches)
 * are significantly taller than the base card; dagre needs to know this
 * so it spaces the ranks far enough apart.
 */
const PORT_HEIGHT_INCREMENT = 44;
const BASE_PORT_COUNT = 2;

/** Inter-rank gap (between graph levels along the layout direction). */
const RANK_SEPARATION = 80;
/** Intra-rank gap (between sibling nodes on the same level). */
const NODE_SEPARATION = 40;

/**
 * Snap a single coordinate to the canvas grid so the laid-out positions
 * line up with manually-placed nodes. Mirrors the `SNAP_GRID` constant
 * in {@code GraphCanvas.tsx}.
 */
const snap = (v: number, step: number): number => Math.round(v / step) * step;

export interface AutoArrangeOptions {
  /** Edge of the graph the layout begins from. Defaults to `LR`. */
  readonly direction?: 'LR' | 'TB' | 'RL' | 'BT';
  /** Per-node hint when caller knows the actual rendered footprint. */
  readonly nodeSize?: { width: number; height: number };
  /** Grid snap, matched to the canvas's `snapGrid`. Defaults to `[20, 20]`. */
  readonly snapGrid?: readonly [number, number];
}

/**
 * Run a left-to-right dagre layout over the given graph and return a new
 * `ProcessNode[]` with every node's `position` replaced by the laid-out
 * coordinates. Edges are read for adjacency only -- their data and
 * order are preserved verbatim by the caller.
 *
 * The function is deterministic for a given (nodes, edges) pair, so the
 * same click on Auto-arrange always produces the same canvas layout.
 * Stable layouts make the action safe to undo/redo without ratcheting.
 */
export function autoArrange(
  nodes: readonly ProcessNode[],
  edges: readonly ProcessEdge[],
  options: AutoArrangeOptions = {},
): ProcessNode[] {
  if (nodes.length === 0) return [];

  const direction = options.direction ?? 'LR';
  const nodeSize = options.nodeSize ?? {
    width: DEFAULT_NODE_WIDTH,
    height: DEFAULT_NODE_HEIGHT,
  };
  const [stepX, stepY] = options.snapGrid ?? [20, 20];

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: direction,
    ranksep: RANK_SEPARATION,
    nodesep: NODE_SEPARATION,
    marginx: 40,
    marginy: 40,
  });
  // dagre expects an `setDefaultEdgeLabel` even when we never set one;
  // returning {} keeps the library happy and the call ergonomic.
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((node) => {
    const measured = (node as Record<string, unknown>).measured as
      | { width?: number; height?: number }
      | undefined;
    const measuredW = measured?.width;
    const measuredH = measured?.height;

    const portCount =
      node.data && 'ports' in node.data && Array.isArray(node.data.ports)
        ? node.data.ports.length
        : 0;
    const estimatedHeight =
      portCount > BASE_PORT_COUNT
        ? nodeSize.height + (portCount - BASE_PORT_COUNT) * PORT_HEIGHT_INCREMENT
        : nodeSize.height;

    g.setNode(node.id, {
      width: measuredW ?? node.width ?? nodeSize.width,
      height: measuredH ?? node.height ?? estimatedHeight,
    });
  });
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  return nodes.map((node) => {
    const laid = g.node(node.id);
    if (!laid) return node;
    // dagre returns the *centre* of the node; React Flow uses the
    // top-left corner. Subtract half the footprint to convert.
    const w = laid.width;
    const h = laid.height;
    return {
      ...node,
      position: {
        x: snap(laid.x - w / 2, stepX),
        y: snap(laid.y - h / 2, stepY),
      },
    };
  });
}
